import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: "default" | "secondary" | "outline-solid" | "ghost" | "destructive";
	size?: "default" | "sm" | "lg" | "icon";
}

export function buttonStyles({
	variant = "default",
	size = "default",
	className,
}: {
	variant?: ButtonProps["variant"];
	size?: ButtonProps["size"];
	className?: string;
}) {
	return cn(
		"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold",
		"transition-all duration-200 ease-out active:scale-[0.98]",
		"focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
		"disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100",
		{
			"hover:bg-primary/90 bg-primary text-primary-foreground shadow-sm hover:shadow-md":
				variant === "default",
			"hover:bg-secondary/80 bg-secondary text-secondary-foreground shadow-xs hover:shadow-sm":
				variant === "secondary",
			"hover:border-primary/40 border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground hover:shadow-sm":
				variant === "outline-solid",
			"hover:bg-accent hover:text-black border border-transparent hover:border-black": variant === "ghost",
			"hover:bg-destructive/90 bg-destructive text-destructive-foreground shadow-xs hover:shadow-md":
				variant === "destructive",
		},
		{
			"h-10 px-4 py-2 text-sm": size === "default",
			"h-9 px-3 text-sm": size === "sm",
			"h-14 px-8 text-base tracking-wide": size === "lg",
			"h-10 w-10 p-0": size === "icon",
		},
		className,
	);
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant = "default", size = "default", ...props }, ref) => {
		return <button ref={ref} className={buttonStyles({ variant, size, className })} {...props} />;
	},
);

Button.displayName = "Button";
